import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonsModule } from '@common/commons.module';
import { ActivatedRoute, Params } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GoogleMapsApiLoaderService } from '@core/helpers/google-maps-api-loader/google-maps-api-loader.service';
import { ROUTE } from '@consts/routes';
import { ERROR } from '@consts/consts';
import { FormattedAddress } from '@core/types/google';
import { GooglePlacesService } from '@core/services/google-places/google-places.service';
import { environment } from 'environments/environment';

@UntilDestroy()
@Component({
  selector: 'app-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.sass'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CommonsModule,
    NgOptimizedImage,
  ],
})
export class WidgetComponent implements OnInit {

  @ViewChild('widgetSearch', { static: true }) searchElementRef: ElementRef;

  form: FormGroup;

  themeColor: string;

  errors: string[] = [];

  encodedPlace: string = '';

  isWealth: boolean = false;

  userName: string = '';

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
    private googleMapsApiLoaderService: GoogleMapsApiLoaderService,
    private googlePlacesService: GooglePlacesService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      address: ['', Validators.required],
      unit: [''],
    });

    this.activatedRoute.queryParams
      .pipe(
        untilDestroyed(this),
      )
      .subscribe((params: Params) => {
        const { hex } = params;
        const { wealth } = params;
        const { user } = params;

        if (user) {
          this.userName = user;
        }

        this.isWealth = !!wealth;
        this.themeColor = `#${hex}`;
      });

    this.initGoogleAutoSuggest();
  }

  submit(): void {

    this.resetError();

    if (!this.form.value?.address) {
      this.errors.push(ERROR.DEFAULT_SEARCH_ERROR);

      return;
    }

    const homeValueRoute: string = this.isWealth ? `/${ROUTE.name.HOME_VALUE}` : '';
    const address: string = encodeURI(this.form.controls.address.value);
    const encodedPlace: string = encodeURI(this.encodedPlace);
    const unit: string = this.form.controls.unit.value ? `&unit=${encodeURI(this.form.controls.unit.value)}` : '';

    const link: string = `${environment.appUrl}/${this.userName}${homeValueRoute}?address=${address}&encodedPlace=${encodedPlace}${unit}`;

    window.open(link, '_parent');
  }

  initGoogleAutoSuggest(): void {
    this
      .googleMapsApiLoaderService
      .load()
      .pipe(
        untilDestroyed(this),
      )
      .subscribe((result: boolean) => {
        if (!result) { return; }

        const autocomplete: google.maps.places.Autocomplete = new google.maps.places.Autocomplete(
          this.searchElementRef.nativeElement, { types: ['address'] },
        );

        autocomplete.setComponentRestrictions({ country: ['us'] });

        const suggest = (): void => {
          this.ngZone.run(() => {
            this.resetError();

            const place: google.maps.places.PlaceResult = autocomplete.getPlace();

            if (!place.address_components) {
              return;
            }

            const address: FormattedAddress = this
              .googlePlacesService
              .remapGooglePlacesAddress(place.address_components);

            const addressValid: boolean = this.googlePlacesService
              .validateGooglePlacesAddress(address, this.form, (error: any) => {
                this.errors = [
                  ...this.errors,
                  ...error,
                ];
              });

            if (!addressValid) { return; }

            const metaGeoData: any = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              place_id: place.place_id,
            };

            this.encodedPlace = btoa(
              JSON.stringify({
                address_components: place.address_components,
                ...metaGeoData,
              }),
            );
          });
        };

        autocomplete.addListener('place_changed', suggest);
      });

  }

  getMarkerClass(): boolean {
    const control: AbstractControl = this.form.controls.address;

    return (!control.valid && control.touched) || !!this.errors.length;
  }

  private resetError(): void {
    this.errors = [];
  }

  clear(): void {
    this.form.reset();
    this.resetError();
  }

}
